import React, { useState, useCallback, memo } from 'react';
import { Calendar, MapPin, Clock, Star, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { SmartImage } from './SmartImage';
import { UpcomingEvent } from '../services/api';

interface EventCardProps {
  event: UpcomingEvent;
  onClick?: () => void;
  imageMode?: 'smart-fit' | 'contain' | 'cover';
}

function EventCardComponent({
  event,
  onClick,
  imageMode = 'smart-fit'
}: EventCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleCardClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else if (event.eventUrl) {
      window.open(event.eventUrl, '_blank', 'noopener,noreferrer');
    }
  }, [onClick, event.eventUrl]);

  const handleImageAnalyzed = useCallback((metadata: any) => {
    setImageLoaded(true);
  }, [event.eventName]);

  // Use thumbnailUrl first, then fallback to imageUrl
  const displayImage = event.thumbnailUrl || event.imageUrl;
  
  // Extract date and time if needed
  const displayDate = event.date || 'Data a confirmar';
  const displayTime = ''; // Time extraction logic can be added later if needed

  return (
    <Card 
      className="group cursor-pointer overflow-hidden card-light-shadow hover-glow-light transition-all duration-300 transform hover:-translate-y-1 border-border/50 hover:border-orange-primary/30 h-full flex flex-col"
      onClick={handleCardClick}
    >
      <div className="relative flex-shrink-0">
        {/* Smart Responsive Image Container */}
        <div className="event-image-container">
          <SmartImage
            src={displayImage}
            alt={event.eventName || 'Evento'}
            className="event-image"
            containerClassName="w-full h-full"
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
            loading="lazy"
            mode={imageMode === 'contain' ? 'always-blur' : 'auto'}
            blurIntensity="subtle"
            onImageAnalyzed={handleImageAnalyzed}
          />
          
          {/* Loading Skeleton */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          
          {/* Image Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      </div>

      <CardContent className="p-4 sm:p-5 flex-1 flex flex-col">
        {/* Title - Fixed Height Container */}
        <div className="h-14 mb-3 flex items-start">
          <h3 className="font-heading font-semibold text-base sm:text-lg line-clamp-2 leading-tight group-hover:text-orange-primary transition-colors">
            {event.eventName || 'Evento sem nome'}
          </h3>
        </div>

        {/* Event Details - Flex Growing Section */}
        <div className="space-y-2 flex-1">
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar size={14} className="mr-2 flex-shrink-0" />
            <span className="truncate">{displayDate}</span>
            {displayTime && (
              <>
                <Clock size={14} className="ml-3 mr-1 flex-shrink-0" />
                <span>{displayTime}</span>
              </>
            )}
          </div>
          
          {event.location && (
            <div className="flex items-start text-sm text-muted-foreground">
              <MapPin size={14} className="mr-2 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">{event.location}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Memoize o componente para evitar re-renders desnecessários
export const EventCard = memo(EventCardComponent);